import type React from 'react';
import { Dispatch, SetStateAction, useState } from 'react';

import { ThirdServiceItem, ThirdServiceResponse } from '@/app/models/detailsThirdService';
import { useUndoUnionThirdServiceMutation } from '@/app/services/detailsThirdServiceApi';
import { formatDate } from '@/app/utils/dateUtils';
import formatterMonetaryValue, { formatCPFAndCNPJ } from '@/app/utils/numberUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import { usePeriod } from '@/context/PeriodContext';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Checkbox, IconButton, Menu, MenuItem, TableCell, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import TruncatedTextWithTooltip from '../../common/truncatedTextWithTooltip';

interface ThirdServicesTableProps {
  selectThirdServices: number[];
  setSelectThirdServices: (id: number[]) => void;
  setDrawerEdit: CallableFunction;
  setDrawerTransfer: CallableFunction;
  setSelectedThirdServicesItem: Dispatch<SetStateAction<ThirdServiceItem | undefined>>;
  selectThirdServicesItem: ThirdServiceItem;
  thirdServiceData: ThirdServiceResponse | undefined;
}

export default function ThirdServicesTable({
  selectThirdServices,
  setSelectThirdServices,
  setDrawerEdit,
  setDrawerTransfer,
  setSelectedThirdServicesItem,
  selectThirdServicesItem,
  thirdServiceData,
}: ThirdServicesTableProps) {
  const theme = useTheme();
  const period = usePeriod();

  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [idUndoUnion, setIdUndoUnion] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { t } = useTranslation(['relatedService', 'common']);

  const handleCheckboxChange = (id: number, checked: boolean) => {
    const newSelectedCollaborators = checked
      ? [...selectThirdServices, id]
      : selectThirdServices.filter((collabId) => collabId !== id);

    setSelectThirdServices(newSelectedCollaborators);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = thirdServiceData?.items?.map((collab) => collab.id);
      setSelectThirdServices(allIds as number[]);
    } else {
      setSelectThirdServices([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrowersEdit = () => {
    setDrawerEdit(true);
    setAnchorEl(null);
  };

  const handleDrawersTransfer = () => {
    setDrawerTransfer(true);
    setAnchorEl(null);
  };

  const [undoUnion] = useUndoUnionThirdServiceMutation();

  const handleUndoUnion = async () => {
    await toast.promise(
      undoUnion({
        id: idUndoUnion as number,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:undo.union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setAnchorEl(null);
  };

  const columns: Column[] = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={
            selectThirdServices.length > 0 && selectThirdServices.length < (thirdServiceData?.items?.length as number)
          }
          checked={
            (thirdServiceData?.items?.length as number) > 0 &&
            selectThirdServices.length === thirdServiceData?.items?.length
          }
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(6),
    },
    { key: 'project', label: t('common:supplier'), width: theme.spacing(50) },
    { key: 'cnpj', label: t('common:cnpj') },
    { key: 'value', label: t('common:total.value') },
    { key: 'expenseDate', label: t('common:expenseDate') },
    { key: 'invoice.number', label: t('common:invoice.number'), width: theme.spacing(20) },
    { key: 'consolidated', label: t('common:consolidated'), align: 'center' },
    { key: 'grouped', label: t('common:grouped'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (item: ThirdServiceItem) => {
    const isSelected = selectThirdServices.includes(item.id);
    return (
      <>
        <TableCell sx={{ ...bodyCellSx, padding: 'checkbox' }}>
          <Checkbox
            color="secondary"
            sx={{ p: 0 }}
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
            inputProps={{ 'aria-labelledby': `Item-${item.id}` }}
          />
        </TableCell>

        <TableCell sx={bodyCellSx}>
          <TruncatedTextWithTooltip value={`${item.supplier ? item.supplier : '---'}`} sx={bodyCellSx} size={40} />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{formatCPFAndCNPJ(item.supplierCode)}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{formatterMonetaryValue(item.value)}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{item.expenseDate ? formatDate(item.expenseDate) : '----'}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>
          <TruncatedTextWithTooltip
            value={`${item.invoiceCode ? item.invoiceCode : '---'}`}
            sx={bodyCellSx}
            size={10}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.consolidated ? (
            <CheckIcon sx={{ color: theme.palette.success.light, cursor: 'pointer' }} />
          ) : (
            <CloseIcon sx={{ color: theme.palette.grey[500], cursor: 'pointer' }} />
          )}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.grouped ? (
            <Tooltip title={t('common:union.result')} arrow placement="bottom-start">
              <LinkIcon sx={{ color: theme.palette.secondary.light, cursor: 'pointer' }} />
            </Tooltip>
          ) : null}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              handleMenuOpen(e, item.id);
              setSelectedThirdServicesItem(item);
              setIdUndoUnion(item.id);
            }}
            aria-label="more actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </>
    );
  };

  // Use the project ID as the unique key
  const getRowKey = (project: ThirdServiceItem) => project.id as number;

  return (
    <>
      <DataTable
        columns={columns}
        data={thirdServiceData?.items || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        size="small"
        maxHeight={theme.spacing(70)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedStatus && (
          <>
            <MenuItem onClick={() => handleDrowersEdit()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon />
                {t('common:edit.detail')}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleDrawersTransfer()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareArrowsIcon />
                {t('common:transfer')}
              </Box>
            </MenuItem>
            {selectThirdServicesItem.grouped && (
              <MenuItem onClick={() => handleUndoUnion()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CallSplitIcon />
                  {t('common:undo.union')}
                </Box>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
}
